import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, ShoppingBag, X, Upload, Pencil, Trash2, Wheat, Milk, Egg, Nut, ChevronDown, Save, Settings } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import CartSidebar from '@/components/CartSidebar';
import CheckoutModal from '@/components/CheckoutModal';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', label: 'Tout' },
  { id: 'entrees', label: 'Entrées' },
  { id: 'plats', label: 'Plats principaux' },
  { id: 'supplements', label: 'Plats supplémentaires' },
  { id: 'desserts', label: 'Desserts' },
];

const DEFAULT_ITEMS = [
  { id: '1', name: 'Potage aux brocolis', description: 'Potage crémeux fait maison', category: 'entrees', image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_gourmet-evolved/artifacts/zqdpyjqy_Potage%20aux%20brocolis.jpg', formats: [{ label: '500 ml', price: 8.00 }, { label: '1 litre', price: 12.00 }], badges: [], allergens: ['gluten', 'lait'] },
  { id: '2', name: "Salade choix de l'équipe", description: 'Laissez notre équipe vous préparer une belle salade pour accompagner vos repas ! Contenant grosseur Poke bowl.', category: 'entrees', image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600&h=400&fit=crop', formats: [{ label: '26 oz', price: 9.45 }], badges: ['nouveau'], allergens: [] },
  { id: '3', name: 'Rouleaux impériaux au poulet', description: 'Salade jardinière', category: 'plats', image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_gourmet-evolved/artifacts/46rc1t88_Rouleaux%20imp%C3%A9riaux%20au%20poulet.jpg', formats: [{ label: 'Adulte', price: 8.95 }, { label: 'Format familial - 4 portions', price: 33.95 }], badges: [], allergens: ['gluten', 'oeufs', 'soja'] },
  { id: '4', name: 'Quiche poireaux-bacon', description: 'Riz aux légumes', category: 'plats', image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_gourmet-evolved/artifacts/zopmwua8_Quiche%20poireaux-bacon.jpg', formats: [{ label: 'Adulte', price: 9.95 }, { label: 'Format familial - 4 portions', price: 37.95 }], badges: [], allergens: ['gluten', 'lait', 'oeufs'] },
  { id: '5', name: 'Boulettes de porc aux pêches', description: 'Purée et légumes', category: 'plats', image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_gourmet-evolved/artifacts/a9447wb5_Boulettes%20de%20porc%20aux%20p%C3%AAches.jpg', formats: [{ label: 'Adulte', price: 9.95 }, { label: 'Format familial - 4 portions', price: 37.95 }], badges: [], allergens: ['gluten'] },
  { id: '6', name: 'Galettes de saumon', description: 'Pommes de terre et légumes', category: 'plats', image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_gourmet-evolved/artifacts/lstrt0d1_Galettes%20de%20saumon.jpg', formats: [{ label: 'Adulte', price: 10.50 }, { label: 'Format familial - 4 portions', price: 39.95 }], badges: [], allergens: ['oeufs'] },
  { id: '7', name: 'Lasagne italienne', description: 'Lasagne maison généreuse', category: 'supplements', image: 'https://images.unsplash.com/photo-1709429790175-b02bb1b19207?w=600&h=400&fit=crop', formats: [{ label: 'Portion adulte', price: 8.95 }, { label: 'Format familial - 4 adultes', price: 33.95 }], badges: [], allergens: ['gluten', 'lait'] },
  { id: '8', name: 'Pâté chinois', description: 'Classique réconfortant', category: 'supplements', image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_gourmet-evolved/artifacts/ctnnu4rb_Pat%C3%A9%20chinois.jpeg', formats: [{ label: 'Portion adulte', price: 8.95 }, { label: 'Format familial - 4 adultes', price: 33.95 }], badges: [], allergens: ['lait'] },
  { id: '9', name: 'Poulet général tao', description: 'Poulet croustillant sauce sucrée', category: 'supplements', image: 'https://images.unsplash.com/photo-1687966699414-095ca9c35593?w=600&h=400&fit=crop', formats: [{ label: 'Portion adulte', price: 9.95 }, { label: 'Format familial - 4 adultes', price: 37.95 }], badges: [], allergens: ['gluten', 'soja'] },
  { id: '10', name: 'Spaghetti italien', description: 'Sauce maison', category: 'supplements', image: 'https://images.unsplash.com/photo-1622973536968-3ead9e780960?w=600&h=400&fit=crop', formats: [{ label: 'Portion adulte', price: 8.95 }, { label: 'Format familial - 4 adultes', price: 33.95 }], badges: [], allergens: ['gluten'] },
  { id: '11', name: 'Brownies', description: 'Brownies chocolatés maison', category: 'desserts', image: 'https://images.unsplash.com/photo-1636743715220-d8f8dd900b87?w=600&h=400&fit=crop', formats: [{ label: '6 unités', price: 7.50 }, { label: '12 unités', price: 13.95 }], badges: [], allergens: ['gluten', 'lait', 'oeufs'] },
  { id: '12', name: 'Assortiment de desserts', description: 'Variété de desserts maison', category: 'desserts', image: 'https://images.unsplash.com/photo-1483695028939-5bb13f8648b0?w=600&h=400&fit=crop', formats: [{ label: '6 unités', price: 7.50 }, { label: '12 unités', price: 13.95 }], badges: [], allergens: ['gluten', 'lait', 'oeufs'] },
];

function ItemCard({ item, onAdd, isAdmin, onEdit, onDelete }) {
  const [showDetail, setShowDetail] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(0);

  useEffect(() => {
    if (showDetail) {
      const handleEsc = (e) => { if (e.key === 'Escape') setShowDetail(false); };
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [showDetail]);

  const allergenIcons = { gluten: Wheat, lait: Milk, oeufs: Egg, soja: Nut };

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer group relative"
        style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}
        onClick={() => setShowDetail(true)}
        data-testid={`menu-item-${item.id}`}
      >
        {isAdmin && (
          <div className="absolute top-2 left-2 z-10 flex gap-1">
            <button onClick={(e) => { e.stopPropagation(); onEdit(item); }}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white shadow-lg transition-all hover:scale-110"
              style={{ backgroundColor: '#3B82F6' }} data-testid={`edit-item-${item.id}`}>
              <Pencil size={14} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white shadow-lg transition-all hover:scale-110"
              style={{ backgroundColor: '#EF4444' }} data-testid={`delete-item-${item.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        )}
        <div className="relative h-44 overflow-hidden">
          {item.image ? (
            <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-muted)' }}>
              <ShoppingBag size={40} style={{ color: 'var(--color-border)' }} />
            </div>
          )}
          {item.badges?.includes('nouveau') && (
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: '#6DAE65' }}>Nouveau</span>
          )}
          {item.badges?.includes('populaire') && (
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: '#F78A7D' }}>Populaire</span>
          )}
          <button onClick={(e) => { e.stopPropagation(); onAdd(item, item.formats[0]); }}
            className="absolute bottom-3 right-3 w-10 h-10 rounded-full flex items-center justify-center text-white shadow-lg transition-all hover:scale-110"
            style={{ backgroundColor: 'var(--color-primary)' }} data-testid={`add-item-${item.id}`}>
            <Plus size={20} />
          </button>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-base mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{item.name}</h3>
          <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--color-muted-fg)' }}>{item.description}</p>
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
              {item.formats[0].price.toFixed(2)} $
            </span>
            {item.formats.length > 1 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-muted)', color: 'var(--color-muted-fg)' }}>
                {item.formats.length} formats
              </span>
            )}
          </div>
        </div>
      </motion.div>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetail && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[90] flex items-center justify-center p-4" onClick={() => setShowDetail(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--color-card)' }}
              onClick={e => e.stopPropagation()}>
              {item.image && <img src={item.image} alt={item.name} className="w-full h-56 object-cover" />}
              <div className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{item.name}</h2>
                    <p className="text-sm mt-1" style={{ color: 'var(--color-muted-fg)' }}>{item.description}</p>
                  </div>
                  <button onClick={() => setShowDetail(false)} className="p-1"><X size={20} /></button>
                </div>

                {item.category === 'desserts' && item.allergens?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {item.allergens.map(a => {
                      const Icon = allergenIcons[a] || Wheat;
                      return <span key={a} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--color-muted)', color: 'var(--color-muted-fg)' }}>
                        <Icon size={10} /> Contient du {a}
                      </span>;
                    })}
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-xs font-medium" style={{ color: 'var(--color-muted-fg)' }}>Choisir le format :</p>
                  {item.formats.map((f, i) => (
                    <button key={i} onClick={() => setSelectedFormat(i)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl text-sm transition-all ${selectedFormat === i ? 'ring-2' : ''}`}
                      style={{
                        backgroundColor: selectedFormat === i ? 'var(--color-primary)' + '15' : 'var(--color-muted)',
                        borderColor: selectedFormat === i ? 'var(--color-primary)' : 'var(--color-border)',
                        border: '1px solid',
                        ringColor: 'var(--color-primary)',
                      }}>
                      <span className="font-medium">{f.label}</span>
                      <span className="font-bold" style={{ color: 'var(--color-primary)' }}>{f.price.toFixed(2)} $</span>
                    </button>
                  ))}
                </div>

                <button onClick={() => { onAdd(item, item.formats[selectedFormat]); setShowDetail(false); }}
                  className="w-full py-3.5 rounded-xl text-white font-semibold transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, #F78A7D, #E8665A)' }}>
                  Ajouter au panier — {item.formats[selectedFormat].price.toFixed(2)} $
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const SECTIONS = [
  { id: 'semaine', label: 'METS CUISINÉS - REPAS EN SEMAINE', categories: ['entrees', 'plats', 'supplements'] },
  { id: 'desserts', label: 'DESSERTS', categories: ['desserts'] },
  { id: 'surgele', label: 'METS CUISINÉS - SECTION SURGELÉE', categories: ['supplements'] },
];

function MenuSection({ section, items, onAdd, defaultOpen = false, isAdmin, onEdit, onDelete }) {
  const [open, setOpen] = useState(defaultOpen);
  const sectionItems = items.filter(i => section.categories.includes(i.category));
  if (sectionItems.length === 0 && !defaultOpen) return null;

  return (
    <div className="mb-6 rounded-2xl overflow-hidden" style={{ border: '1px solid var(--color-border)' }}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left transition-colors hover:bg-[var(--color-muted)]"
        data-testid={`section-${section.id}`}>
        <h2 className="text-lg sm:text-xl font-semibold tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {section.label}
        </h2>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"
          className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--color-muted-fg)' }}>
          <path d="M5 7l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <div className="px-5 pb-6">
          {CATEGORIES.filter(c => c.id !== 'all' && section.categories.includes(c.id)).map(cat => {
            const catItems = sectionItems.filter(i => i.category === cat.id);
            if (catItems.length === 0) return null;
            return (
              <div key={cat.id} className="mb-8">
                <h3 className="text-xl font-semibold mb-4 pb-2 border-b flex items-center gap-2"
                  style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--color-primary)', borderColor: 'var(--color-border)' }}>
                  {cat.label}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {catItems.map(item => (
                    <ItemCard key={item.id} item={item} onAdd={onAdd} isAdmin={isAdmin} onEdit={onEdit} onDelete={onDelete} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EditItemModal({ item, onSave, onClose }) {
  const [form, setForm] = useState({
    name: item?.name || '', description: item?.description || '', category: item?.category || 'plats',
    image: item?.image || '',
    formats: item?.formats || [{ label: 'Adulte', price: 0 }], badges: item?.badges || [], allergens: item?.allergens || [],
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const updateFormat = (i, key, val) => {
    const fmts = [...form.formats];
    fmts[i] = { ...fmts[i], [key]: key === 'price' ? parseFloat(val) || 0 : val };
    setForm(f => ({ ...f, formats: fmts }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const token = localStorage.getItem('admin_token');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({ ...f, image: `${API}${data.url}` }));
    } catch (err) {
      alert('Erreur upload: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const labelCls = "block text-[11px] uppercase tracking-wider font-semibold mb-1.5";
  const inputCls = "w-full px-4 py-3 rounded-xl text-sm outline-none";
  const inputStyle = { backgroundColor: 'var(--color-muted)', border: '1px solid var(--color-border)', color: 'var(--color-fg)' };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-[95] flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
        className="w-full max-w-lg rounded-2xl overflow-y-auto max-h-[85vh] p-6 space-y-4"
        style={{ backgroundColor: 'var(--color-card)' }} onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {item ? 'Modifier le plat' : 'Nouveau plat'}
          </h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {/* Photo */}
        <div>
          <p className={labelCls} style={{ color: 'var(--color-muted-fg)' }}>Photo du plat</p>
          {form.image && (
            <img src={form.image} alt="Apercu" className="w-full h-36 object-cover rounded-xl mb-2" />
          )}
          <div className="flex gap-2">
            <input type="url" placeholder="Coller un lien d'image" value={form.image}
              onChange={e => setForm(f => ({ ...f, image: e.target.value }))}
              data-testid="edit-item-image" className="flex-1 px-4 py-2.5 rounded-xl text-sm outline-none"
              style={inputStyle} />
            <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={handleFileUpload} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-105 whitespace-nowrap disabled:opacity-50"
              style={{ backgroundColor: '#6DAE65' }}
              data-testid="upload-item-image">
              {uploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Upload size={14} /> Importer</>}
            </button>
          </div>
        </div>

        {/* Nom */}
        <div>
          <p className={labelCls} style={{ color: 'var(--color-muted-fg)' }}>Nom du plat</p>
          <input type="text" placeholder="Ex: Potage aux brocolis" value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className={inputCls} style={inputStyle} />
        </div>

        {/* Description */}
        <div>
          <p className={labelCls} style={{ color: 'var(--color-muted-fg)' }}>Description</p>
          <input type="text" placeholder="Ex: Potage cremeux fait maison" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            className={inputCls} style={inputStyle} />
        </div>

        {/* Catégorie */}
        <div>
          <p className={labelCls} style={{ color: 'var(--color-muted-fg)' }}>Categorie</p>
          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            className={inputCls} style={inputStyle}>
            {CATEGORIES.filter(c => c.id !== 'all').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        {/* Formats et prix */}
        <div>
          <p className={labelCls} style={{ color: 'var(--color-muted-fg)' }}>Formats et prix</p>
          {form.formats.map((f, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <div className="flex-1">
                {i === 0 && <span className="text-[10px] mb-0.5 block" style={{ color: 'var(--color-muted-fg)' }}>Format</span>}
                <input type="text" placeholder="Ex: Adulte" value={f.label} onChange={e => updateFormat(i, 'label', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={inputStyle} />
              </div>
              <div className="w-24">
                {i === 0 && <span className="text-[10px] mb-0.5 block" style={{ color: 'var(--color-muted-fg)' }}>Prix ($)</span>}
                <input type="number" step="0.01" placeholder="0.00" value={f.price} onChange={e => updateFormat(i, 'price', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={inputStyle} />
              </div>
              {form.formats.length > 1 && (
                <button onClick={() => setForm(f2 => ({ ...f2, formats: f2.formats.filter((_, j) => j !== i) }))}
                  className="p-2 rounded-lg text-red-400 hover:bg-red-400/10 self-end"><Trash2 size={14} /></button>
              )}
            </div>
          ))}
          <button onClick={() => setForm(f => ({ ...f, formats: [...f.formats, { label: '', price: 0 }] }))}
            className="text-xs font-medium px-3 py-1.5 rounded-lg" style={{ color: 'var(--color-primary)', backgroundColor: 'rgba(247,138,125,0.1)' }}>
            + Ajouter un format
          </button>
        </div>

        <button onClick={() => onSave({ ...form, id: item?.id })}
          className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #3B82F6, #2563EB)' }}>
          <Save size={16} /> Sauvegarder
        </button>
      </motion.div>
    </motion.div>
  );
}

export default function Commande() {
  const { addItem, count, setOpen } = useCart();
  const [menuItems, setMenuItems] = useState(DEFAULT_ITEMS);
  const [weeklyImage, setWeeklyImage] = useState('https://customer-assets-gfyr7b9c.emergentagent.net/job_gourmet-evolved/artifacts/kjjxj4ll_image.png');
  const [showCheckout, setShowCheckout] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWeekly, setEditingWeekly] = useState(false);
  const [weeklyInput, setWeeklyInput] = useState('');
  const [weeklyUploading, setWeeklyUploading] = useState(false);
  const weeklyFileRef = useRef(null);

  useEffect(() => {
    const admin = localStorage.getItem('admin_token');
    setIsAdmin(!!admin);
  }, []);

  const loadItems = () => {
    axios.get(`${API}/api/menu-items`).then(r => {
      if (r.data.length > 0) setMenuItems(r.data);
    }).catch(() => {});
  };

  useEffect(() => {
    loadItems();
    axios.get(`${API}/api/content/weekly_menu`).then(r => {
      if (r.data?.data?.image) setWeeklyImage(r.data.data.image);
    }).catch(() => {});
  }, []);

  const getAdminToken = () => localStorage.getItem('admin_token');

  const handleSaveItem = async (data) => {
    const token = getAdminToken();
    if (!token) return;
    try {
      if (data.id) {
        await axios.put(`${API}/api/menu-items/${data.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/api/menu-items`, data, { headers: { Authorization: `Bearer ${token}` } });
      }
      loadItems();
      setShowEditModal(false);
      setEditItem(null);
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Supprimer ce plat ?')) return;
    const token = getAdminToken();
    if (!token) return;
    try {
      await axios.delete(`${API}/api/menu-items/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      loadItems();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveWeeklyImage = async (url) => {
    const imageUrl = url || weeklyInput;
    if (!imageUrl) return;
    const token = getAdminToken();
    if (!token) return;
    try {
      await axios.post(`${API}/api/content/weekly_menu`, { data: { image: imageUrl } }, { headers: { Authorization: `Bearer ${token}` } });
      setWeeklyImage(imageUrl);
      setEditingWeekly(false);
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleWeeklyFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setWeeklyUploading(true);
    const token = getAdminToken();
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const fullUrl = `${API}${data.url}`;
      setWeeklyInput(fullUrl);
      await handleSaveWeeklyImage(fullUrl);
    } catch (err) {
      alert('Erreur upload: ' + (err.response?.data?.detail || err.message));
    }
    setWeeklyUploading(false);
  };

  return (
    <div data-testid="commande-page" className="min-h-screen">
      {/* Header */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 pt-8 pb-4">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] mb-2 font-semibold" style={{ color: 'var(--color-primary)' }}>Commande en ligne</p>
              <h1 className="text-3xl sm:text-4xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                METS CUISINÉS
              </h1>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted-fg)' }}>
                Mets cuisinés faits maison disponibles pour emporter ou livrer les MARDIS ! TOUS SANS ARACHIDE !
              </p>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <button onClick={() => { setEditItem(null); setShowEditModal(true); }}
                  data-testid="admin-add-item"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:scale-105"
                  style={{ backgroundColor: '#3B82F6' }}>
                  <Plus size={16} /> Ajouter un plat
                </button>
                <button onClick={() => { localStorage.removeItem('admin_token'); setIsAdmin(false); }}
                  data-testid="admin-logout"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold text-white transition-all hover:scale-105 cursor-pointer"
                  style={{ backgroundColor: '#EF4444' }}>
                  <X size={12} /> Quitter Admin
                </button>
              </div>
            )}
          </div>

          {weeklyImage && (
            <div className="rounded-2xl overflow-hidden shadow-lg mb-8 max-w-3xl mx-auto relative group" style={{ border: '1px solid var(--color-border)' }}>
              <img src={weeklyImage} alt="Menu de la semaine" className="w-full" data-testid="weekly-menu-image" />
              {isAdmin && !editingWeekly && (
                <button onClick={() => { setWeeklyInput(weeklyImage); setEditingWeekly(true); }}
                  className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-all duration-300 cursor-pointer"
                  data-testid="admin-change-weekly">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 px-5 py-3 rounded-xl text-white text-sm font-semibold"
                    style={{ backgroundColor: 'rgba(59,130,246,0.9)' }}>
                    <Pencil size={16} /> Changer la photo
                  </span>
                </button>
              )}
              {isAdmin && editingWeekly && (
                <div className="absolute inset-x-0 bottom-0 p-4 space-y-2" style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}>
                  <div className="flex gap-2">
                    <input type="url" placeholder="Coller l'URL de la nouvelle image" value={weeklyInput}
                      onChange={e => setWeeklyInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Escape') setEditingWeekly(false); if (e.key === 'Enter') handleSaveWeeklyImage(); }}
                      className="flex-1 px-4 py-2.5 rounded-xl text-sm text-white outline-none"
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}
                      autoFocus />
                    <button onClick={() => handleSaveWeeklyImage()}
                      className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: '#3B82F6' }}>
                      <Save size={16} />
                    </button>
                    <button onClick={() => setEditingWeekly(false)}
                      className="px-3 py-2.5 rounded-xl text-sm text-white/60 hover:text-white">
                      <X size={16} />
                    </button>
                  </div>
                  <div className="flex justify-center">
                    <input type="file" ref={weeklyFileRef} accept="image/*" className="hidden" onChange={handleWeeklyFileUpload} />
                    <button onClick={() => weeklyFileRef.current?.click()} disabled={weeklyUploading}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-105 disabled:opacity-50"
                      style={{ backgroundColor: '#6DAE65' }}>
                      {weeklyUploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Upload size={14} /> Importer de l'appareil</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Menu Sections */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        {SECTIONS.map((section, i) => (
          <MenuSection key={section.id} section={section} items={menuItems} onAdd={addItem} defaultOpen={i === 0}
            isAdmin={isAdmin} onEdit={(item) => { setEditItem(item); setShowEditModal(true); }} onDelete={handleDeleteItem} />
        ))}
      </section>

      <CartSidebar onCheckout={() => setShowCheckout(true)} />
      <CheckoutModal open={showCheckout} onClose={() => setShowCheckout(false)} />

      {/* Admin Edit Modal */}
      <AnimatePresence>
        {showEditModal && (
          <EditItemModal item={editItem} onSave={handleSaveItem} onClose={() => { setShowEditModal(false); setEditItem(null); }} />
        )}
      </AnimatePresence>

      {/* Floating cart button */}
      <button onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[70] w-14 h-14 rounded-full flex items-center justify-center text-white shadow-2xl transition-all hover:scale-110"
        style={{ background: 'linear-gradient(135deg, #F78A7D, #E8665A)', boxShadow: '0 4px 20px rgba(247,138,125,0.4)' }}
        data-testid="cart-btn">
        <ShoppingBag size={24} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
            {count}
          </span>
        )}
      </button>
    </div>
  );
}
